import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Brain, Plus, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { SafeArea } from "@/components/ios/SafeArea";
import { useColonies } from "@/hooks/useColonies";

export default function Landing() {
  const navigate = useNavigate();
  const { colonies, isLoading } = useColonies();
  const [brainPulse, setBrainPulse] = useState(false);
  
  const handleBrainTap = () => {
    setBrainPulse(true);
    setTimeout(() => setBrainPulse(false), 300);
    navigate("/control-room");
  };

  const handleSelectColony = (colonyId: string) => {
    navigate(`/chat/${colonyId}`);
  };

  const handleCreateColony = () => {
    navigate("/add-colony");
  };

  return (
    <SafeArea className="min-h-screen bg-background has-tabbar-padding flex flex-col">
      {/* Centered content at natural eye level */}
      <main className="flex-1 flex flex-col items-center justify-center px-6">
        <div className="flex flex-col items-center -mt-16">
          {/* Larger, more prominent title */}
          <h1 className="text-4xl font-bold text-foreground mb-8 tracking-tight">BrainColonies</h1>
          
          {/* Brain icon at natural focal point */}
          <div 
            className={`mb-10 cursor-pointer transition-all duration-300 ${
              brainPulse ? 'scale-110' : 'hover:scale-105'
            }`}
            onClick={handleBrainTap}
          >
            <div className="relative">
              <Brain 
                size={140} 
                className="text-brain-glow drop-shadow-[0_0_30px_rgba(255,255,255,0.4)] transition-all duration-500" 
              />
              <div className="absolute inset-0 animate-pulse">
                <Brain 
                  size={140} 
                  className="text-brain-glow/20" 
                />
              </div>
            </div>
          </div>
        </div>

        <div className="w-full max-w-sm space-y-3">
          <p className="text-center ios-callout text-muted-foreground mb-6">
            Or jump directly to a colony:
          </p>
          {(colonies || []).map((colony) => (
            <Button 
              key={colony.id}
              className="w-full bg-ios-surface-secondary hover:bg-ios-surface-tertiary text-foreground border border-ios-separator transition-all duration-200 active:scale-[0.98] h-12"
              variant="ghost"
              onClick={() => handleSelectColony(colony.id)}
            >
              {colony.name}
            </Button>
          ))}
          <Button 
            variant="outline" 
            className="w-full bg-transparent border border-ios-separator hover:bg-ios-surface-secondary text-muted-foreground hover:text-foreground flex items-center justify-center gap-2 transition-all duration-200 active:scale-[0.98] h-12"
            onClick={handleCreateColony}
          >
            <Plus size={18} />
            New Colony
          </Button>
        </div>

        {isLoading && (
          <div className="flex justify-center items-center p-4">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        )}

        <button 
          onClick={() => navigate("/chat/1")} 
          className="mt-10 underline text-muted-foreground"
        >
          Skip to Colonies
        </button>

        <p className="mt-6 ios-footnote text-muted-foreground">Tap the brain to open Control Room</p>
      </main>
    </SafeArea>
  );
}